import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { MonthlyBurdenChartProps } from './MonthlyBurdenChart.types'

// Helper function to format large numbers for axis (e.g., 10000 -> 10k)
const formatAxisNumbers = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`
  }
  return value.toFixed(0)
}

// Helper function to format price in euros
const formatPrice = (euros: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(euros)
}

// Helper function to format price with +/- prefix
const getFormattedPrice = (amount: number): string => {
  if (amount > 0) {
    return `+${formatPrice(amount)}`
  }
  return formatPrice(amount * -1)
}

// Helper function to calculate monthly savings for a decade
const getMonthlySavings = (chartsData: ChartDataPoint[]): number => {
  return (
    chartsData.reduce(
      (acc, curr) =>
        acc +
        curr.monthlyEnergyCosts -
        curr.monthlyEnergyCostsAppliedRetrofit -
        (curr.monthlyAnnuity || 0),
      0
    ) / 10
  )
}

// Helper function to calculate decade-wise monthly savings
const getMonthlyDecadeWiseSavings = (chartsData: ChartDataPoint[]) => {
  const firstDecade = chartsData.slice(0, 10)
  const secondDecade = chartsData.slice(10, 20)
  const thirdDecade = chartsData.slice(20, 30)

  return {
    firstDecade: Math.round(getMonthlySavings(firstDecade)),
    secondDecade: Math.round(getMonthlySavings(secondDecade)),
    thirdDecade: Math.round(getMonthlySavings(thirdDecade)),
  }
}

// Chart data type
type ChartDataPoint = {
  year: number
  monthlyEnergyCosts: number
  monthlyEnergyCostsAppliedRetrofit: number
  monthlyAnnuity?: number
}

// Custom tooltip component
type CustomTooltipProps = {
  active?: boolean
  payload?: Array<{
    value: number
    payload: ChartDataPoint
  }>
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload[0]) {
    const data = payload[0].payload

    return (
      <Box
        sx={{
          padding: '8px',
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0px 3px 9px 0px rgba(0, 0, 0, 0.18)',
          width: '240px',
        }}
      >
        <Typography
          sx={{
            marginBottom: '4px',
            fontWeight: 700,
            color: '#6a707e',
            fontSize: '12px',
          }}
        >
          {data.year}
        </Typography>

        {/* Section Header */}
        <Typography
          sx={{
            color: '#74777F',
            fontSize: '14px',
            fontWeight: 700,
            marginBottom: '4px',
          }}
        >
          ⌀ monatliche Kosten
        </Typography>

        {/* Monatliche Energiekosten unsaniert */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#366192',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten unsaniert:</span>
          <span>{formatPrice(data.monthlyEnergyCosts)}</span>
        </Box>

        {/* Monatliche Energiekosten saniert */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#0E8C4A',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten saniert:</span>
          <span>{formatPrice(data.monthlyEnergyCostsAppliedRetrofit)}</span>
        </Box>

        {/* Monatliche Finanzierungskosten */}
        <Box
          sx={{
            color: '#A48105',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Finanzierungskosten:</span>
          <span>{formatPrice(data.monthlyAnnuity ?? 0)}</span>
        </Box>

        {/* Monatliche Belastung */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#181d29',
            paddingTop: '4px',
            marginTop: '4px',
            borderTop: '1px solid #74777f',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Monatliche Belastung</span>
          <span>
            {formatPrice(
              data.monthlyEnergyCosts -
                data.monthlyEnergyCostsAppliedRetrofit -
                (data.monthlyAnnuity ?? 0)
            )}
          </span>
        </Box>
      </Box>
    )
  }
  return null
}

const MonthlyBurdenChart = ({
  energyCostsResults,
  selectedFinancingResult,
}: MonthlyBurdenChartProps) => {
  // Process data for chart - convert to monthly values
  const chartData = useMemo(() => {
    const { annuityInCt: perYearAnnuityInCt, loanPeriod } = selectedFinancingResult

    return energyCostsResults.map((item, index) => {
      // Calculate monthly annuity (only during loan period)
      let thisYearAnnuity = 0
      if (index < loanPeriod) {
        thisYearAnnuity = Math.round(perYearAnnuityInCt / 100 / 12)
      }

      return {
        year: item.year,
        monthlyEnergyCosts: Math.round(item.energyCostsInCt / 100 / 12),
        monthlyEnergyCostsAppliedRetrofit: Math.round(
          item.energyCostsAppliedRetrofitInCt / 100 / 12
        ),
        ...(thisYearAnnuity ? { monthlyAnnuity: thisYearAnnuity } : {}),
      }
    })
  }, [energyCostsResults, selectedFinancingResult])

  // Calculate decade-wise savings
  const decadeSavings = useMemo(() => {
    return getMonthlyDecadeWiseSavings(chartData)
  }, [chartData])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            borderRadius: '8px',
            padding: '8px',
            flex: 1,
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>
              {getFormattedPrice(decadeSavings.firstDecade)}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>/Monat</Typography>
          </Box>
          <Typography sx={{ color: '#464A54', fontWeight: 500, fontSize: '11px' }}>
            Durchschnittlich der nächsten 1–10 Jahre
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            borderRadius: '8px',
            padding: '8px',
            flex: 1,
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>
              {getFormattedPrice(decadeSavings.secondDecade)}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>/Monat</Typography>
          </Box>
          <Typography sx={{ color: '#464A54', fontWeight: 500, fontSize: '11px' }}>
            Durchschnittlich der nächsten 10–20 Jahre
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            borderRadius: '8px',
            padding: '8px',
            flex: 1,
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>
              {getFormattedPrice(decadeSavings.thirdDecade)}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>/Monat</Typography>
          </Box>
          <Typography sx={{ color: '#464A54', fontWeight: 500, fontSize: '11px' }}>
            Durchschnittlich der nächsten 20–30 Jahre
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          height: '372px',
          '& *:focus': {
            outline: 'none',
          },
          '& svg': {
            outline: 'none',
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 12,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="1 0" vertical={false} stroke="#E1E1E4" />
            <XAxis
              strokeWidth={0}
              dataKey="year"
              tick={{ fill: '#74777F', fontSize: 12 }}
              tickLine={false}
            />
            <YAxis
              strokeWidth={0}
              width={45}
              tickFormatter={formatAxisNumbers}
              tick={{ fill: '#74777F', fontSize: 12 }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Energiekosten saniert - green, stacked */}
            <Area
              type="monotone"
              dataKey="monthlyEnergyCostsAppliedRetrofit"
              stroke="#82ca9d"
              fill="#1CFB8E55"
              stackId="1"
            />

            {/* Energiekosten unsaniert - blue, not stacked */}
            <Area type="monotone" dataKey="monthlyEnergyCosts" stroke="#5084C9" fill="#6BA7FF55" />

            {/* Finanzierungskosten - yellow, stacked */}
            <Area
              type="monotone"
              dataKey="monthlyAnnuity"
              stroke="#5BC06B"
              fill="#EDC74C"
              stackId="1"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}

export default MonthlyBurdenChart
